import React, { useState, useEffect } from 'react';

/**
 * VehicleForm Component
 * Form for adding and editing vehicles
 */
export default function VehicleForm({
  vehicle,
  truckTypes,
  availableDevices,
  users,
  onSubmit,
  onCancel,
  submitting,
  t,
  translate
}) {
  const isEditing = !!vehicle;

  // Form state
  const [formData, setFormData] = useState({
    user_id: '',
    license_plate: '',
    plate_type: '',
    vehicle_type: '',
    customer_type: 'Normal',
    truck_type_id: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    vehicle_model: '',
    vehicle_color: '',
    owner_name: '',
    owner_phone: '',
    owner_address: '',
    owner_district: '',
    owner_province: '',
    owner_postal_code: '',
    owner_country: 'Laos',
    device_type: 'mifare',
    device_id: '',
    mifare_card_uid: '',
    obu_sbu_id: '',
    status: 'active',
    registered_at: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [deviceType, setDeviceType] = useState('mifare');
  const [mergedAvailableDevices, setMergedAvailableDevices] = useState({
    mifare: [],
    obu_sbu: []
  });
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedProvinceId, setSelectedProvinceId] = useState('');
  const [plateTypes, setPlateTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [colors, setColors] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [colorSearch, setColorSearch] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [mifareSearch, setMifareSearch] = useState('');
  const [obuSearch, setObuSearch] = useState('');

  // Loading & error states for each data source
  const [loading, setLoading] = useState({
    provinces: false,
    plateTypes: false,
    brands: false,
    colors: false,
    districts: false
  });
  const [loadError, setLoadError] = useState({
    provinces: false,
    plateTypes: false,
    brands: false,
    colors: false
  });

  // Initialize form data when editing
  useEffect(() => {
    if (vehicle) {
      setFormData({
        user_id: vehicle.user_id ? String(vehicle.user_id) : '',
        license_plate: vehicle.license_plate || '',
        plate_type: vehicle.plate_type || '',
        vehicle_type: vehicle.vehicle_type || '',
        customer_type: vehicle.customer_type || 'Normal',
        truck_type_id: vehicle.truck_type_id ? String(vehicle.truck_type_id) : '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || new Date().getFullYear(),
        color: vehicle.color || vehicle.vehicle_color || '',
        vehicle_model: vehicle.vehicle_model || vehicle.model || '',
        vehicle_color: vehicle.color || vehicle.vehicle_color || '',
        owner_name: vehicle.owner_name || '',
        owner_phone: vehicle.owner_phone || '',
        owner_address: vehicle.owner_address || '',
        owner_district: vehicle.owner_district || '',
        owner_province: vehicle.owner_province || '',
        owner_postal_code: vehicle.owner_postal_code || '',
        owner_country: vehicle.owner_country || 'Laos',
        device_type: vehicle.device_type || 'mifare',
        device_id: vehicle.device_id || '',
        mifare_card_uid: vehicle.mifare_card_uid || vehicle.device_id || '',
        obu_sbu_id: vehicle.obu_sbu_id || vehicle.device_id || '',
        status: vehicle.status || 'active',
        registered_at: vehicle.registered_at ? vehicle.registered_at.split('T')[0] : new Date().toISOString().split('T')[0],
        notes: vehicle.notes || ''
      });
      setDeviceType(vehicle.device_type || 'mifare');
    }
  }, [vehicle]);

  // Set initial province ID when editing and provinces are loaded
  useEffect(() => {
    if (vehicle && vehicle.owner_province && provinces.length > 0 && !selectedProvinceId) {
      const matchedProvince = provinces.find(p => p.name === vehicle.owner_province);
      if (matchedProvince) {
        setSelectedProvinceId(String(matchedProvince.id));
      }
    }
  }, [vehicle, provinces, selectedProvinceId]);

  // Merge current vehicle's device into available devices when editing
  useEffect(() => {
    const merged = {
      mifare: [...availableDevices.mifare],
      obu_sbu: [...availableDevices.obu_sbu]
    };

    // If editing and vehicle has a mifare card, ensure it's in the list
    if (vehicle && vehicle.device_type === 'mifare' && vehicle.mifare_card_uid) {
      const hasMifareCard = merged.mifare.some(card => card.card_uid === vehicle.mifare_card_uid);
      if (!hasMifareCard) {
        // Add the current mifare card to the list
        merged.mifare.unshift({
          id: vehicle.id, // Use vehicle ID as placeholder
          card_uid: vehicle.mifare_card_uid,
          serial_no: vehicle.serial_no || '', // Include serial_no if available
          balance: 0, // We don't have balance info, use 0 as placeholder
          status: 'assigned'
        });
      }
    }

    // If editing and vehicle has an OBU device, ensure it's in the list
    if (vehicle && vehicle.device_type === 'obu_sbu' && vehicle.obu_sbu_id) {
      const hasObuDevice = merged.obu_sbu.some(device => device.device_id === vehicle.obu_sbu_id);
      if (!hasObuDevice) {
        // Add the current OBU device to the list
        merged.obu_sbu.unshift({
          id: vehicle.id, // Use vehicle ID as placeholder
          device_id: vehicle.obu_sbu_id,
          serial_no: vehicle.serial_no || '', // Include serial_no if available
          device_name: vehicle.obu_sbu_id, // Use device_id as name placeholder
          status: 'assigned'
        });
      }
    }

    // Sort mifare cards by serial_no
    merged.mifare.sort((a, b) => {
      const serialA = a.serial_no || '';
      const serialB = b.serial_no || '';
      return serialA.localeCompare(serialB, undefined, { numeric: true });
    });

    // Sort OBU devices by serial_no
    merged.obu_sbu.sort((a, b) => {
      const serialA = a.serial_no || '';
      const serialB = b.serial_no || '';
      return serialA.localeCompare(serialB, undefined, { numeric: true });
    });

    setMergedAvailableDevices(merged);
  }, [vehicle, availableDevices]);

  // Vehicle types
  const vehicleTypes = [
    { key: 'ລົດເກັ່ງນ້ອຍຂະຫນາດນ້ອຍ', label: 'ລົດເກັ່ງນ້ອຍຂະຫນາດນ້ອຍ' },
    { key: 'ລົດເກັ່ງນ້ອຍ (<7 ບ່ອນນັ່ງ), ລົດບັນທຸກຂະຫນາດນ້ອຍ', label: 'ລົດເກັ່ງນ້ອຍ (<7 ບ່ອນນັ່ງ), ລົດບັນທຸກຂະຫນາດນ້ອຍ' },
    { key: 'ລົດໂດຍສານຂະຫນາດນ້ອຍ (7-12 ບ່ອນນັ່ງ)', label: 'ລົດໂດຍສານຂະຫນາດນ້ອຍ (7-12 ບ່ອນນັ່ງ)' },
    { key: 'ລົດໂດຍສານຂະຫນາດກາງ (12-24 ບ່ອນນັ່ງ)', label: 'ລົດໂດຍສານຂະຫນາດກາງ (12-24 ບ່ອນນັ່ງ)' },
    { key: 'ລົດໂດຍສານຂະຫນາດໃຫຍ່ (>24 ບ່ອນນັ່ງ)', label: 'ລົດໂດຍສານຂະຫນາດໃຫຍ່ (>24 ບ່ອນນັ່ງ)' },
    { key: 'ລົດບັນທຸກ 6 ລໍ້', label: 'ລົດບັນທຸກ 6 ລໍ້' },
    { key: 'ລົດບັນທຸກ 10 ລໍ້', label: 'ລົດບັນທຸກ 10 ລໍ້' },
    { key: 'ລົດບັນທຸກ 10 ລໍ້ ຂຶ້ນໄປ', label: 'ລົດບັນທຸກ 10 ລໍ້ ຂຶ້ນໄປ' }
  ];

  // Fetch provinces, districts, plate types, brands, and colors from API
  useEffect(() => {
    fetchProvinces();
    fetchPlateTypes();
    fetchBrands();
    fetchColors();
  }, []);

  // Fetch districts when province changes
  useEffect(() => {
    if (selectedProvinceId) {
      fetchDistricts(selectedProvinceId);
    } else {
      setDistricts([]);
      setFormData(prev => ({ ...prev, owner_district: '' }));
    }
  }, [selectedProvinceId]);

  const fetchProvinces = async () => {
    setLoading(prev => ({ ...prev, provinces: true }));
    setLoadError(prev => ({ ...prev, provinces: false }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/provinces`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProvinces(data.data || data);
      } else {
        setLoadError(prev => ({ ...prev, provinces: true }));
      }
    } catch (error) {
      console.error('Error fetching provinces:', error);
      setLoadError(prev => ({ ...prev, provinces: true }));
    } finally {
      setLoading(prev => ({ ...prev, provinces: false }));
    }
  };

  const fetchPlateTypes = async () => {
    setLoading(prev => ({ ...prev, plateTypes: true }));
    setLoadError(prev => ({ ...prev, plateTypes: false }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/plate-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlateTypes(data || []);
      } else {
        setLoadError(prev => ({ ...prev, plateTypes: true }));
      }
    } catch (error) {
      console.error('Error fetching plate types:', error);
      setLoadError(prev => ({ ...prev, plateTypes: true }));
    } finally {
      setLoading(prev => ({ ...prev, plateTypes: false }));
    }
  };

  const fetchDistricts = async (provinceId) => {
    setLoading(prev => ({ ...prev, districts: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/provinces/${provinceId}/districts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const districtsArray = Array.isArray(data) ? data : (data.data || []);
        setDistricts(districtsArray);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    } finally {
      setLoading(prev => ({ ...prev, districts: false }));
    }
  };

  const fetchBrands = async () => {
    setLoading(prev => ({ ...prev, brands: true }));
    setLoadError(prev => ({ ...prev, brands: false }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/brands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBrands(data.data || data || []);
      } else {
        setLoadError(prev => ({ ...prev, brands: true }));
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      setLoadError(prev => ({ ...prev, brands: true }));
    } finally {
      setLoading(prev => ({ ...prev, brands: false }));
    }
  };

  const fetchColors = async () => {
    setLoading(prev => ({ ...prev, colors: true }));
    setLoadError(prev => ({ ...prev, colors: false }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/colors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setColors(data.data || data || []);
      } else {
        setLoadError(prev => ({ ...prev, colors: true }));
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
      setLoadError(prev => ({ ...prev, colors: true }));
    } finally {
      setLoading(prev => ({ ...prev, colors: false }));
    }
  };

  // Retry all failed fetches
  const retryFailedFetches = () => {
    if (loadError.provinces) fetchProvinces();
    if (loadError.plateTypes) fetchPlateTypes();
    if (loadError.brands) fetchBrands();
    if (loadError.colors) fetchColors();
  };

  // Computed: is any initial data still loading?
  const isInitialLoading = loading.provinces || loading.plateTypes || loading.brands || loading.colors;
  // Computed: did any fetch fail?
  const hasAnyError = loadError.provinces || loadError.plateTypes || loadError.brands || loadError.colors;

  // Inline spinner component
  const Spinner = ({ size = 'sm' }) => (
    <svg
      className={`animate-spin ${size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} text-blue-500 inline-block`}
      xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );

  // Skeleton row for dropdowns
  const SkeletonOption = () => (
    <div className="animate-pulse flex items-center gap-2 px-2 py-1">
      <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-3/4" />
    </div>
  );

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle province change
  const handleProvinceChange = (value) => {
    const selectedProvince = provinces.find(p => String(p.id) === String(value));
    setSelectedProvinceId(value);
    setFormData(prev => ({
      ...prev,
      owner_province: selectedProvince ? selectedProvince.name : '',
      owner_district: '' // Reset district when province changes
    }));
  };

  // Handle district change
  const handleDistrictChange = (value) => {
    const selectedDistrict = districts.find(d => String(d.id) === String(value));
    setFormData(prev => ({
      ...prev,
      owner_district: selectedDistrict ? selectedDistrict.name : ''
    }));
  };

  // Handle device type change
  const handleDeviceTypeChange = (type) => {
    setDeviceType(type);
    setFormData(prev => ({
      ...prev,
      device_type: type,
      mifare_card_uid: type === 'mifare' ? prev.mifare_card_uid : '',
      obu_sbu_id: type === 'obu_sbu' ? prev.obu_sbu_id : ''
    }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Prepare data for API
    const selectedDeviceId = deviceType === 'mifare' ? formData.mifare_card_uid : formData.obu_sbu_id;
    const truckTypeIdValue = formData.truck_type_id && formData.truck_type_id !== ''
      ? parseInt(formData.truck_type_id, 10)
      : null;

    const vehicleData = {
      user_id: formData.user_id && formData.user_id !== '' ? parseInt(formData.user_id, 10) : null,
      license_plate: formData.license_plate,
      plate_type: formData.plate_type || null,
      vehicle_type: formData.vehicle_type,
      customer_type: formData.customer_type,
      truck_type_id: truckTypeIdValue,
      make: formData.make || null,
      model: formData.model || null,
      year: formData.year ? parseInt(formData.year, 10) : new Date().getFullYear(),
      color: formData.color || null,
      vehicle_model: formData.model || formData.vehicle_model || null,
      vehicle_color: formData.color || formData.vehicle_color || null,
      owner_name: formData.owner_name,
      owner_phone: formData.owner_phone || null,
      owner_address: formData.owner_address || null,
      owner_district: formData.owner_district || null,
      owner_province: formData.owner_province || null,
      owner_postal_code: formData.owner_postal_code || null,
      owner_country: formData.owner_country || 'Laos',
      device_type: formData.device_type,
      device_id: selectedDeviceId,
      mifare_card_uid: deviceType === 'mifare' ? selectedDeviceId : null,
      obu_sbu_id: deviceType === 'obu_sbu' ? selectedDeviceId : null,
      status: formData.status || 'active',
      registered_at: formData.registered_at || new Date().toISOString().split('T')[0],
      notes: formData.notes || null
    };

    // Debug: Log the data being sent
    console.log('=== VehicleForm: Submitting Data ===');
    console.log('Form Data:', formData);
    console.log('Vehicle Data to Send:', vehicleData);
    console.log('plate_type:', vehicleData.plate_type);
    console.log('customer_type:', vehicleData.customer_type);
    console.log('make:', vehicleData.make);
    console.log('model:', vehicleData.model);
    console.log('year:', vehicleData.year, '(type:', typeof vehicleData.year, ')');
    console.log('color:', vehicleData.color);
    console.log('truck_type_id:', vehicleData.truck_type_id, '(type:', typeof vehicleData.truck_type_id, ')');

    onSubmit(vehicleData);
  };

  // Check if selected vehicle type is a truck type that requires truck_type_id
  const truckVehicleTypes = [
    'ລົດບັນທຸກ 6 ລໍ້',
    'ລົດບັນທຸກ 10 ລໍ້',
    'ລົດບັນທຸກ 10 ລໍ້ ຂຶ້ນໄປ'
  ];
  const isTruckType = truckVehicleTypes.includes(formData.vehicle_type);

  return (
    <div className="mb-6 p-4 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
      <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">
        {isEditing ? `✏️ ${t('vehicles.edit_vehicle_form_title')}` : `➕ ${t('vehicles.add_vehicle_form_title')}`}
      </h3>

      {/* ── Loading banner ── */}
      {isInitialLoading && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg text-sm text-blue-700 dark:text-blue-300">
          <svg className="animate-spin h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>ກຳລັງໂຫລດຂໍ້ມູນ… ກະລຸນາລໍຖ້າສັກຄູ່ (Loading form data, please wait…)</span>
        </div>
      )}

      {/* ── Error / retry banner ── */}
      {!isInitialLoading && hasAnyError && (
        <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-300">
          <span>⚠️ ບາງຂໍ້ມູນໂຫລດບໍ່ສຳເລັດ — ກະລຸນາລອງໃໝ່ (Some data failed to load, please retry)</span>
          <button
            type="button"
            onClick={retryFailedFetches}
            className="flex-shrink-0 px-3 py-1 bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700 text-red-700 dark:text-red-200 rounded font-medium transition-colors"
          >
            🔄 Retry
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Basic Vehicle Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* License Plate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('vehicles.license_plate')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              placeholder={t('vehicles.license_plate_placeholder')}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
              value={formData.license_plate}
              onChange={(e) => handleChange('license_plate', e.target.value.toUpperCase())}
            />
          </div>

          {/* Plate Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              🏷️ Plate Type
              {loading.plateTypes && <Spinner />}
            </label>
            <select
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white disabled:opacity-60"
              value={formData.plate_type}
              onChange={(e) => handleChange('plate_type', e.target.value)}
              disabled={loading.plateTypes}
            >
              {loading.plateTypes
                ? <option value="">⏳ Loading plate types…</option>
                : loadError.plateTypes
                  ? <option value="">⚠️ Failed to load — click Retry</option>
                  : <>
                      <option value="">-- Select Plate Type --</option>
                      {plateTypes.map(pt => (
                        <option key={pt.id} value={pt.name_lao || pt.name}>
                          {pt.icon} {pt.name_lao || pt.name}
                        </option>
                      ))}
                    </>
              }
            </select>
          </div>

          {/* Vehicle Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('vehicles.vehicle_type')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
              value={formData.vehicle_type}
              onChange={(e) => {
                handleChange('vehicle_type', e.target.value);
                handleChange('truck_type_id', '');
              }}
            >
              <option value="">{t('vehicles.vehicle_types.select_type')}</option>
              {vehicleTypes.map(type => (
                <option key={type.key} value={type.key}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Customer Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Customer Type <span className="text-red-500">*</span>
            </label>
            <select
              required
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
              value={formData.customer_type}
              onChange={(e) => handleChange('customer_type', e.target.value)}
            >
              <option value="Normal">Normal</option>
              <option value="VIP">VIP</option>
            </select>
          </div>

          {/* Truck Type - Only show for truck vehicle types */}
          {isTruckType && (
            <div className="lg:col-span-2 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                🚚 Truck Type <span className="text-red-500">*</span>
              </label>
              <select
                required
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
                value={formData.truck_type_id}
                onChange={(e) => handleChange('truck_type_id', e.target.value)}
              >
                <option value="">-- Select Truck Classification --</option>
                {truckTypes.map(tt => (
                  <option key={tt.id} value={String(tt.id)}>
                    {tt.code} - {tt.name_lao} ({tt.wheels} wheels, {tt.axles} axles)
                  </option>
                ))}
              </select>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                ℹ️ Required for truck vehicles - Select the appropriate truck classification
              </p>
            </div>
          )}

          {/* Make (Brand) - Searchable Dropdown */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Make (Brand)
              {loading.brands && <Spinner />}
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={loading.brands ? '⏳ Loading brands…' : 'Search or select brand...'}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white disabled:opacity-60"
                value={brandSearch || formData.make}
                disabled={loading.brands}
                onChange={(e) => {
                  setBrandSearch(e.target.value);
                  setShowBrandDropdown(true);
                }}
                onFocus={() => setShowBrandDropdown(true)}
              />
              {formData.make && !loading.brands && (
                <button
                  type="button"
                  onClick={() => { handleChange('make', ''); setBrandSearch(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            {showBrandDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="sticky top-0 bg-gray-100 dark:bg-gray-800 p-2 border-b border-gray-200 dark:border-gray-600">
                  <div className="flex flex-wrap gap-1">
                    {['All', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')].map(letter => (
                      <button key={letter} type="button"
                        onClick={() => setBrandSearch(letter === 'All' ? '' : letter)}
                        className={`px-2 py-1 text-xs rounded ${
                          brandSearch.toUpperCase() === letter || (letter === 'All' && !brandSearch)
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                        }`}
                      >{letter}</button>
                    ))}
                  </div>
                </div>
                <div>
                  {loading.brands ? (
                    [1,2,3,4].map(i => <SkeletonOption key={i} />)
                  ) : loadError.brands ? (
                    <div className="px-3 py-4 text-center text-sm text-red-500">
                      ⚠️ Failed to load brands.
                      <button type="button" onClick={fetchBrands} className="ml-2 underline text-blue-500">Retry</button>
                    </div>
                  ) : (
                    <>
                      {brands
                        .filter(b => b.is_active !== false && (brandSearch === '' ||
                          b.brand_name?.toLowerCase().startsWith(brandSearch.toLowerCase()) ||
                          b.brand_name?.toLowerCase().includes(brandSearch.toLowerCase())))
                        .map(brand => (
                          <button key={brand.brand_id} type="button"
                            onClick={() => { handleChange('make', brand.brand_name); setBrandSearch(''); setShowBrandDropdown(false); }}
                            className={`w-full text-left px-3 py-2 hover:bg-blue-100 dark:hover:bg-blue-900 ${
                              formData.make === brand.brand_name ? 'bg-blue-50 dark:bg-blue-800' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-gray-900 dark:text-white">{brand.brand_name}</span>
                              {brand.country && <span className="text-xs text-gray-500 dark:text-gray-400">{brand.country}</span>}
                            </div>
                          </button>
                        ))}
                      {brands.filter(b => b.is_active !== false && (brandSearch === '' ||
                        b.brand_name?.toLowerCase().startsWith(brandSearch.toLowerCase()) ||
                        b.brand_name?.toLowerCase().includes(brandSearch.toLowerCase()))).length === 0 && (
                        <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">No brands found</div>
                      )}
                    </>
                  )}
                </div>
                <div className="sticky bottom-0 bg-gray-100 dark:bg-gray-800 p-2 border-t border-gray-200 dark:border-gray-600">
                  <button type="button" onClick={() => setShowBrandDropdown(false)}
                    className="w-full py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >Close ▲</button>
                </div>
              </div>
            )}
            {showBrandDropdown && (
              <div className="fixed inset-0 z-40" onClick={() => setShowBrandDropdown(false)} />
            )}
          </div>

          {/* Model */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Model
            </label>
            <input
              type="text"
              placeholder="e.g., Camry, Civic, F-150"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
              value={formData.model}
              onChange={(e) => {
                handleChange('model', e.target.value);
                handleChange('vehicle_model', e.target.value);
              }}
            />
          </div>

          {/* Year */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Year
            </label>
            <input
              type="number"
              min="1900"
              max={new Date().getFullYear() + 1}
              placeholder="e.g., 2024"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
              value={formData.year}
              onChange={(e) => handleChange('year', parseInt(e.target.value) || new Date().getFullYear())}
            />
          </div>

          {/* Color - Searchable Dropdown */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Color
              {loading.colors && <Spinner />}
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={loading.colors ? '⏳ Loading colors…' : 'Search or select color...'}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white disabled:opacity-60"
                value={colorSearch || formData.color}
                disabled={loading.colors}
                onChange={(e) => { setColorSearch(e.target.value); setShowColorDropdown(true); }}
                onFocus={() => setShowColorDropdown(true)}
              />
              {formData.color && (
                <button type="button"
                  onClick={() => { handleChange('color', ''); handleChange('vehicle_color', ''); setColorSearch(''); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >✕</button>
              )}
            </div>
            {showColorDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div className="sticky top-0 bg-gray-100 dark:bg-gray-800 p-2 border-b border-gray-200 dark:border-gray-600">
                  <div className="flex flex-wrap gap-1">
                    {['All', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')].map(letter => (
                      <button key={letter} type="button"
                        onClick={() => setColorSearch(letter === 'All' ? '' : letter)}
                        className={`px-2 py-1 text-xs rounded ${
                          colorSearch.toUpperCase() === letter || (letter === 'All' && !colorSearch)
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                        }`}
                      >{letter}</button>
                    ))}
                  </div>
                </div>
                <div>
                  {loading.colors ? (
                    [1,2,3,4].map(i => <SkeletonOption key={i} />)
                  ) : loadError.colors ? (
                    <div className="px-3 py-4 text-center text-sm text-red-500">
                      ⚠️ Failed to load colors.
                      <button type="button" onClick={fetchColors} className="ml-2 underline text-blue-500">Retry</button>
                    </div>
                  ) : (
                    <>
                      {colors
                        .filter(c => c.is_active !== false && (colorSearch === '' ||
                          c.color_name?.toLowerCase().startsWith(colorSearch.toLowerCase()) ||
                          c.color_name?.toLowerCase().includes(colorSearch.toLowerCase())))
                        .map(color => (
                          <button key={color.color_id} type="button"
                            onClick={() => { handleChange('color', color.color_name); handleChange('vehicle_color', color.color_name); setColorSearch(''); setShowColorDropdown(false); }}
                            className={`w-full text-left px-3 py-2 hover:bg-blue-100 dark:hover:bg-blue-900 ${
                              formData.color === color.color_name ? 'bg-blue-50 dark:bg-blue-800' : ''
                            }`}
                          >
                            <div className="flex items-center">
                              {color.hex_code && (
                                <div className="w-5 h-5 rounded-full mr-2 border border-gray-300 dark:border-gray-500" style={{ backgroundColor: color.hex_code }} />
                              )}
                              <span className="text-gray-900 dark:text-white">{color.color_name}</span>
                              {color.color_code && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({color.color_code})</span>}
                            </div>
                          </button>
                        ))}
                      {colors.filter(c => c.is_active !== false && (colorSearch === '' ||
                        c.color_name?.toLowerCase().startsWith(colorSearch.toLowerCase()) ||
                        c.color_name?.toLowerCase().includes(colorSearch.toLowerCase()))).length === 0 && (
                        <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">No colors found</div>
                      )}
                    </>
                  )}
                </div>
                <div className="sticky bottom-0 bg-gray-100 dark:bg-gray-800 p-2 border-t border-gray-200 dark:border-gray-600">
                  <button type="button" onClick={() => setShowColorDropdown(false)}
                    className="w-full py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >Close ▲</button>
                </div>
              </div>
            )}
            {showColorDropdown && (
              <div className="fixed inset-0 z-40" onClick={() => setShowColorDropdown(false)} />
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              required
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <option value="active">🟢 Active</option>
              <option value="inactive">🔴 Inactive</option>
              <option value="suspended">⚠️ Suspended</option>
              <option value="maintenance">🔧 Maintenance</option>
            </select>
          </div>

          {/* Registered Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Registered Date
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
              value={formData.registered_at}
              onChange={(e) => handleChange('registered_at', e.target.value)}
            />
          </div>
        </div>

        {/* Owner Information */}
        <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
          <h4 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-300">👤 Owner Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Owner Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('vehicles.owner_name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder={t('vehicles.owner_name_placeholder')}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                value={formData.owner_name}
                onChange={(e) => handleChange('owner_name', e.target.value)}
              />
            </div>

            {/* Owner Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('vehicles.owner_phone')}
              </label>
              <input
                type="tel"
                placeholder={t('vehicles.owner_phone_placeholder')}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                value={formData.owner_phone}
                onChange={(e) => handleChange('owner_phone', e.target.value)}
              />
            </div>

            {/* Owner Address */}
            <div className="lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('vehicles.owner_address')}
              </label>
              <input
                type="text"
                placeholder={t('vehicles.owner_address_placeholder')}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                value={formData.owner_address}
                onChange={(e) => handleChange('owner_address', e.target.value)}
              />
            </div>

            {/* Province */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('vehicles.owner_province')}
                {loading.provinces && <Spinner />}
              </label>
              <select
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white disabled:opacity-60"
                value={selectedProvinceId}
                onChange={(e) => handleProvinceChange(e.target.value)}
                disabled={loading.provinces}
              >
                {loading.provinces
                  ? <option value="">⏳ Loading provinces…</option>
                  : loadError.provinces
                    ? <option value="">⚠️ Failed to load — click Retry</option>
                    : <>
                        <option value="">{t('vehicles.select_province')}</option>
                        {provinces.map(province => (
                          <option key={province.id} value={province.id}>
                            {province.name_lao || province.name}
                          </option>
                        ))}
                      </>
                }
              </select>
            </div>

            {/* District */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                City/District
                {loading.districts && <Spinner />}
              </label>
              <select
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white disabled:opacity-60"
                value={districts.find(d => d.name === formData.owner_district || d.name_lao === formData.owner_district || d.name_en === formData.owner_district)?.id || ''}
                onChange={(e) => handleDistrictChange(e.target.value)}
                disabled={!selectedProvinceId || loading.districts}
              >
                {!selectedProvinceId
                  ? <option value="">Select Province First</option>
                  : loading.districts
                    ? <option value="">⏳ Loading districts…</option>
                    : districts.length === 0
                      ? <option value="">No districts available</option>
                      : <>
                          <option value="">Select District</option>
                          {districts.map(district => (
                            <option key={district.id} value={district.id}>
                              {district.name_lao || district.name_en || district.name || `District ${district.id}`}
                            </option>
                          ))}
                        </>
                }
              </select>
              {selectedProvinceId && !loading.districts && districts.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">ບໍ່ມີຂໍ້ມູນເຂດສຳລັບແຂວງນີ້</p>
              )}
            </div>

            {/* Postal Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('vehicles.owner_postal_code')}
              </label>
              <input
                type="text"
                placeholder={t('vehicles.owner_postal_code_placeholder')}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                value={formData.owner_postal_code}
                onChange={(e) => handleChange('owner_postal_code', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Device Information */}
        <div className="border-t border-gray-300 dark:border-gray-600 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300">💳 Device Information</h4>
            <span className="text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">
              ℹ️ Optional – can be assigned later when editing
            </span>
          </div>

          {/* Device Type Selection */}
          <div className="flex space-x-4 mb-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="device_type"
                value="mifare"
                checked={deviceType === 'mifare'}
                onChange={(e) => handleDeviceTypeChange(e.target.value)}
                className="mr-2"
              />
              💳 {t('vehicles.mifare_card')}
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="device_type"
                value="obu_sbu"
                checked={deviceType === 'obu_sbu'}
                onChange={(e) => handleDeviceTypeChange(e.target.value)}
                className="mr-2"
              />
              📡 {t('vehicles.obu_sbu_device')}
            </label>
          </div>

          {/* Device Selection – searchable by Serial No */}
          {deviceType === 'mifare' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('vehicles.mifare_card')}
                <span className="ml-1 text-xs font-normal text-gray-400">(Optional)</span>
              </label>

              {/* Search Box */}
              <div className="relative mb-2">
                <input
                  type="text"
                  placeholder="🔍 Search by Serial No..."
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white text-sm"
                  value={mifareSearch}
                  onChange={(e) => setMifareSearch(e.target.value)}
                />
                {mifareSearch && (
                  <button
                    type="button"
                    onClick={() => setMifareSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >✕</button>
                )}
              </div>

              {/* Selected Card display */}
              {formData.mifare_card_uid && (
                <div className="mb-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded text-sm text-green-700 dark:text-green-300 flex items-center justify-between">
                  <span>✅ Selected: {(() => {
                    const card = mergedAvailableDevices.mifare.find(c => c.card_uid === formData.mifare_card_uid);
                    return card ? `${card.serial_no ? card.serial_no + ' | ' : ''}${card.card_uid}` : formData.mifare_card_uid;
                  })()}</span>
                  <button
                    type="button"
                    onClick={() => { handleChange('mifare_card_uid', ''); handleChange('device_id', ''); }}
                    className="ml-2 text-red-500 hover:text-red-700 font-bold"
                  >✕ Remove</button>
                </div>
              )}

              {/* Card List */}
              <div className="border border-gray-300 dark:border-gray-600 rounded max-h-48 overflow-y-auto">
                {/* None option */}
                <button
                  type="button"
                  onClick={() => { handleChange('mifare_card_uid', ''); handleChange('device_id', ''); }}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                    !formData.mifare_card_uid ? 'bg-gray-100 dark:bg-gray-700 font-medium' : ''
                  }`}
                >
                  <span className="text-gray-500 dark:text-gray-400">— None / Assign Later —</span>
                </button>

                {mergedAvailableDevices.mifare
                  .filter(card => {
                    if (!mifareSearch) return true;
                    const q = mifareSearch.toLowerCase();
                    return (
                      (card.serial_no && card.serial_no.toLowerCase().includes(q)) ||
                      card.card_uid.toLowerCase().includes(q)
                    );
                  })
                  .map(card => (
                    <button
                      key={`mifare-${card.card_uid}`}
                      type="button"
                      onClick={() => { handleChange('mifare_card_uid', card.card_uid); handleChange('device_id', card.card_uid); setMifareSearch(''); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                        formData.mifare_card_uid === card.card_uid ? 'bg-blue-50 dark:bg-blue-900/30 font-medium' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          {card.serial_no && (
                            <span className="font-mono font-semibold text-blue-600 dark:text-blue-400 mr-2">{card.serial_no}</span>
                          )}
                          <span className="text-gray-600 dark:text-gray-300 text-xs">{card.card_uid}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-500">{parseFloat(card.balance || 0).toLocaleString()} LAK</span>
                          {card.status === 'assigned' && (
                            <span className="px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">Current</span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}

                {mergedAvailableDevices.mifare.filter(card => {
                  if (!mifareSearch) return true;
                  const q = mifareSearch.toLowerCase();
                  return (card.serial_no && card.serial_no.toLowerCase().includes(q)) || card.card_uid.toLowerCase().includes(q);
                }).length === 0 && (
                  <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                    No MIFARE cards found matching "{mifareSearch}"
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('vehicles.obu_sbu_device')}
                <span className="ml-1 text-xs font-normal text-gray-400">(Optional)</span>
              </label>

              {/* Search Box */}
              <div className="relative mb-2">
                <input
                  type="text"
                  placeholder="🔍 Search by Serial No..."
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white text-sm"
                  value={obuSearch}
                  onChange={(e) => setObuSearch(e.target.value)}
                />
                {obuSearch && (
                  <button
                    type="button"
                    onClick={() => setObuSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >✕</button>
                )}
              </div>

              {/* Selected Device display */}
              {formData.obu_sbu_id && (
                <div className="mb-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded text-sm text-green-700 dark:text-green-300 flex items-center justify-between">
                  <span>✅ Selected: {(() => {
                    const dev = mergedAvailableDevices.obu_sbu.find(d => d.device_id === formData.obu_sbu_id);
                    return dev ? `${dev.serial_no ? dev.serial_no + ' | ' : ''}${dev.device_id}` : formData.obu_sbu_id;
                  })()}</span>
                  <button
                    type="button"
                    onClick={() => { handleChange('obu_sbu_id', ''); handleChange('device_id', ''); }}
                    className="ml-2 text-red-500 hover:text-red-700 font-bold"
                  >✕ Remove</button>
                </div>
              )}

              {/* OBU Device List */}
              <div className="border border-gray-300 dark:border-gray-600 rounded max-h-48 overflow-y-auto">
                {/* None option */}
                <button
                  type="button"
                  onClick={() => { handleChange('obu_sbu_id', ''); handleChange('device_id', ''); }}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 ${
                    !formData.obu_sbu_id ? 'bg-gray-100 dark:bg-gray-700 font-medium' : ''
                  }`}
                >
                  <span className="text-gray-500 dark:text-gray-400">— None / Assign Later —</span>
                </button>

                {mergedAvailableDevices.obu_sbu
                  .filter(device => {
                    if (!obuSearch) return true;
                    const q = obuSearch.toLowerCase();
                    return (
                      (device.serial_no && device.serial_no.toLowerCase().includes(q)) ||
                      device.device_id.toLowerCase().includes(q)
                    );
                  })
                  .map(device => (
                    <button
                      key={`obu-${device.device_id}`}
                      type="button"
                      onClick={() => { handleChange('obu_sbu_id', device.device_id); handleChange('device_id', device.device_id); setObuSearch(''); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                        formData.obu_sbu_id === device.device_id ? 'bg-blue-50 dark:bg-blue-900/30 font-medium' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          {device.serial_no && (
                            <span className="font-mono font-semibold text-blue-600 dark:text-blue-400 mr-2">{device.serial_no}</span>
                          )}
                          <span className="text-gray-600 dark:text-gray-300 text-xs">{device.device_id}</span>
                        </div>
                        {device.status === 'assigned' && (
                          <span className="text-xs px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">Current</span>
                        )}
                      </div>
                    </button>
                  ))}

                {mergedAvailableDevices.obu_sbu.filter(device => {
                  if (!obuSearch) return true;
                  const q = obuSearch.toLowerCase();
                  return (device.serial_no && device.serial_no.toLowerCase().includes(q)) || device.device_id.toLowerCase().includes(q);
                }).length === 0 && (
                  <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                    No OBU devices found matching "{obuSearch}"
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('vehicles.notes')}
          </label>
          <textarea
            rows="3"
            placeholder={t('vehicles.notes_placeholder')}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
          />
        </div>

        {/* Form Actions */}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={submitting}
            className={`py-2 px-4 rounded transition-colors duration-200 ${submitting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-500 hover:bg-green-600'
              } text-white`}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditing ? 'Updating...' : 'Saving...'}
              </span>
            ) : (
              <span>{isEditing ? `✅ ${t('vehicles.update_vehicle')}` : `✅ ${t('vehicles.add_vehicle_button')}`}</span>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded transition-colors duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            ❌ {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
